import {
  Box,
  Button,
  Container,
  Heading,
  SimpleGrid,
  Text,
  VStack,
  Icon,
  useColorModeValue,
  Stack,
  Flex,
} from '@chakra-ui/react';
import React from 'react';
import { Helmet } from 'react-helmet';
import { BsTelegram, BsYoutube } from "react-icons/bs";
import { FaWhatsapp } from "react-icons/fa";
import { MdOutlineSupportAgent } from "react-icons/md";
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const MotionBox = motion(Box);

const Support = () => {
  // Theme Hooks
  const bg = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.600", "gray.400");
  const headingColor = useColorModeValue("gray.800", "white");

  const supportChannels = [
    {
      name: "Customer Support",
      icon: MdOutlineSupportAgent,
      color: "blue.500",
      link: "https://telegram.me/Techyindiaorg_Helpdesk",
      description: "Get instant help from our support team for any issues.",
    },
    {
      name: "Telegram Channel",
      icon: BsTelegram,
      color: "telegram.500",
      link: "https://telegram.me/TechyindiaorgOfficialnews",
      description: "Join our official channel for updates and announcements.",
    },
    {
      name: "WhatsApp",
      icon: FaWhatsapp,
      color: "green.500",
      link: "https://youtube.com/@techyindiaofficial?si=_F5GXXLlc78yxMWC", // Kept link as per original file, though label says Whatsapp
      description: "Connect with us on WhatsApp for quick queries.",
    },
    {
      name: "YouTube",
      icon: BsYoutube,
      color: "red.500",
      link: "https://youtube.com/@techyindiaofficial?si=_F5GXXLlc78yxMWC",
      description: "Watch tutorials and guides on our YouTube channel.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Support | Techy India</title>
      </Helmet>
      <Box minH="100vh" bg={bg} w="100%" p={{ base: 4, md: 8 }}>
        <Container maxW="container.xl">
          <VStack spacing={12} align="center">

            {/* Header Section */}
            <VStack spacing={4} textAlign="center">
              <Heading
                as="h1"
                size="2xl"
                bgGradient="linear(to-r, brand.400, brand.600)"
                bgClip="text"
                fontWeight="extrabold"
              >
                We're Here to Help
              </Heading>
              <Text fontSize="lg" color={textColor} maxW="2xl">
                Have questions or need assistance? Reach out to us through any of our support channels below. We are dedicated to providing you with the best experience.
              </Text>
            </VStack>

            {/* Support Cards Grid */}
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8} w="full">
              {supportChannels.map((channel, index) => (
                <MotionBox
                  key={index}
                  whileHover={{ y: -10 }}
                  transition={{ duration: 0.3 }}
                  bg={cardBg}
                  p={8}
                  rounded="2xl"
                  shadow="lg"
                  border="1px solid"
                  borderColor={borderColor}
                  textAlign="center"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="space-between"
                  h="full"
                >
                  <VStack spacing={4}>
                    <Flex
                      w={16}
                      h={16}
                      align="center"
                      justify="center"
                      rounded="full"
                      bg={`${channel.color.split('.')[0]}.50`}
                      color={channel.color}
                      mb={2}
                    >
                      <Icon as={channel.icon} w={8} h={8} />
                    </Flex>
                    <Heading size="md" color={headingColor}>
                      {channel.name}
                    </Heading>
                    <Text color={textColor} fontSize="sm">
                      {channel.description}
                    </Text>
                  </VStack>
                  <Button
                    mt={6}
                    w="full"
                    colorScheme={channel.color.split('.')[0]}
                    variant="outline"
                    rightIcon={<Icon as={channel.icon} />}
                    onClick={() => window.open(channel.link, "_blank")}
                    _hover={{
                      bg: channel.color,
                      color: "white",
                      transform: "scale(1.05)",
                      shadow: "md",
                    }}
                    transition="all 0.2s"
                  >
                    Connect
                  </Button>
                </MotionBox>
              ))}
            </SimpleGrid>

            {/* FAQ Section Placeholder (Optional - can be re-enabled if needed) */}
            <Box
              w="full"
              bg={cardBg}
              p={8}
              rounded="2xl"
              shadow="md"
              border="1px solid"
              borderColor={borderColor}
              textAlign="center"
            >
              <Heading size="lg" mb={4} color={headingColor}>
                Still have questions?
              </Heading>
              <Text color={textColor} mb={6}>
                Check out our Frequently Asked Questions for quick answers to common queries.
              </Text>
              <Button
                colorScheme="brand"
                size="lg"
                onClick={() => window.open("/faq", "_self")} // Assuming internal routing
              >
                Visit FAQ
              </Button>
            </Box>

            {/* Policy Links Footer */}
            <Stack align="center" spacing={1} pb={4}>
              <Text fontSize="xs" color={textColor}>
                By continuing, you agree to:
              </Text>
              <Stack direction="row" spacing={2} align="center">
                <Link to="/privacy">
                  <Text fontSize="xs" color="brand.500" fontWeight="semibold" _hover={{ textDecoration: "underline" }}>Privacy</Text>
                </Link>
                <Text fontSize="xs" color={textColor}>|</Text>
                <Link to="/terms">
                  <Text fontSize="xs" color="brand.500" fontWeight="semibold" _hover={{ textDecoration: "underline" }}>Terms</Text>
                </Link>
                <Text fontSize="xs" color={textColor}>|</Text>
                <Link to="/refund-policy">
                  <Text fontSize="xs" color="brand.500" fontWeight="semibold" _hover={{ textDecoration: "underline" }}>Refund</Text>
                </Link>
              </Stack>
            </Stack>

          </VStack>
        </Container>
      </Box>
    </>
  );
};

export default Support;
